<template>
  <v-container fluid class="sales-report-page pa-2 pa-sm-3">
    <v-row dense>
      <v-col cols="12">

        <v-sheet class="pa-3">
          <v-row dense align="center">
            <v-col cols="12" lg="2">
              <div class="text-h6 text-sm-h5">{{ $t('delivery.salesReport.title') }}</div>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <branch-combobox
                  v-model="branch"
                  return-object solo hide-details clearable
              ></branch-combobox>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <product-category-combobox
                v-model="productCategories" multiple
                return-object solo hide-details clearable select-all
              ></product-category-combobox>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <date-type-autocomplete
                v-model="dateType" solo hide-details clearable
              ></date-type-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="2">
              <month-picker v-model="date" solo></month-picker>
            </v-col>

            <v-col
                v-if="$store.getters.hasPermission('QUANTITY_SALE')"
                cols="6"
                sm="3"
                md="2"
                lg="auto"
            >
              <v-checkbox
                  v-model="cantidad"
                  :label="$t('delivery.salesReport.fields.quantity')"
                  class="ma-0"
                  hide-details
              ></v-checkbox>
            </v-col>

            <v-col
                v-if="$store.getters.hasPermission('AMOUNT_SALE')"
                cols="6"
                sm="3"
                md="2"
                lg="auto"
            >
              <v-checkbox
                  v-model="monto"
                  :label="$t('delivery.salesReport.fields.amounts')"
                  class="ma-0"
                  hide-details
              ></v-checkbox>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="auto" class="ml-lg-auto">
              <v-btn
                  @click="fetchReport"
                  :loading="loading"
                  color="primary"
                  class="sales-report-action"
                  outlined
                  block
              >
                {{ $t('delivery.salesReport.actions.generate') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-sheet>

      </v-col>

      <v-col cols="12" v-if="reportWarning">
        <v-alert type="warning" dense text class="mb-0">
          {{ reportWarning }}
        </v-alert>
      </v-col>

      <v-col cols="12" v-if="ready">
        <div class="sales-report-table-wrapper">
          <v-simple-table
              dense
              class="sales-report-table"
              :fixed-header="$vuetify.breakpoint.mdAndUp"
              :height="$vuetify.breakpoint.mdAndUp ? tableHeight : undefined"
          >
            <thead>
            <tr>
              <th class="text-center">{{ $t('delivery.salesReport.table.day') }}</th>
              <th class="text-center">{{ $t('delivery.salesReport.table.date') }}</th>
              <th class="text-center" v-if="$store.getters.hasPermission('AMOUNT_SALE')">{{ $t('delivery.salesReport.table.delivery') }}</th>
              <th class="text-center" v-if="$store.getters.hasPermission('AMOUNT_SALE')">{{ $t('delivery.salesReport.table.products') }}</th>
              <th class="text-center">{{ $t('delivery.salesReport.table.quantity') }}</th>
              <th class="text-center">{{ $t('delivery.salesReport.table.dozens') }}</th>
              <!--<th class="text-center">Prom</th>-->
              <th class="text-center" v-for="product in products" :key="product.id">
                {{ product.shortName }}
              </th>
            </tr>
            </thead>

            <tbody>
            <tr v-for="date in getDates" :key="date" :class="getDay(date) == 'Dom' ? 'border_bottom' : ''">
              <td class="text-center">{{ getDay(date) }}</td>
              <td class="text-center">{{ date.substring(8) }}</td>
              <td class="text-center" v-if="$store.getters.hasPermission('AMOUNT_SALE')">
                <span>{{ currencyFormat(getDeliveryCostByDate(date)) }}</span>
              </td>
              <td class="text-center" v-if="$store.getters.hasPermission('AMOUNT_SALE')">
                <span>{{ currencyFormat(getAmountByDate(date)) }}</span>
              </td>


              <td class="text-center">
                <span>{{ getQuantityByDate(date) }}</span>
              </td>
              <td class="text-center">
                <span>{{ getDozenByDate(date) }}</span>
              </td>
              <td
                  v-for="product in products"
                  :key="product.id"
                  class="text-center "
                  :class="getBackgroundByDate(date, product.shortName)"
              >
                <span v-if="cantidad">
                  {{ getQuantityByDateAndProduct(date, product.shortName) }}
                </span>


                <br v-if="cantidad && monto">
                <span v-if="monto">${{ getAmountByDateAndProduct(date, product.shortName) }}</span>
              </td>

            </tr>

            <tr>
              <td class="text-center">{{ $t('delivery.salesReport.table.total') }}</td>
              <td class="text-center">{{ $t('delivery.salesReport.table.month') }}</td>
              <td class="text-center" v-if="$store.getters.hasPermission('AMOUNT_SALE')">
                <span>{{ currencyFormat(getDeliveryCost) }}</span>
              </td>
              <td class="text-center" v-if="$store.getters.hasPermission('AMOUNT_SALE')">
                <span>{{ currencyFormat(getAmount) }}</span>
              </td>

              <td class="text-center">
                <span>{{ getQuantity }}</span>
              </td>
              <td class="text-center">
                <span>{{ getDozen }}</span>
              </td>


              <td
                  v-for="product in products"
                  :key="product.id"
                  class="text-center "
                  :class="getBackground(product.shortName)"
              >
                <span v-if="cantidad">{{ getQuantityByProduct(product.shortName) }}</span>
                <br v-if="cantidad && monto">
                <span v-if="monto">${{ getAmountByProduct(product.shortName) }}</span>
              </td>

            </tr>


            </tbody>

          </v-simple-table>
        </div>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import SalesReportProvider from "@/modules/delivery/providers/SalesReportProvider";
import BranchProvider from "@/modules/delivery/providers/BranchProvider";
import {Dayjs} from '@dracul/dayjs-frontend'
import MonthPicker from "@/modules/delivery/components/MonthPicker/MonthPicker";
import BranchCombobox from "../../../combobox/BranchCombobox";
import ProductCategoryCombobox from "../../../combobox/ProductCategoryCombobox/ProductCategoryCombobox.vue";
import DateTypeAutocomplete from "./DateTypeAutocomplete.vue";
import StockProvider from "@/modules/delivery/providers/StockProvider";
import CurrencyMixin from "@/modules/delivery/mixins/CurrencyMixin";

export default {
  name: "SalesReportPage",
  components: {BranchCombobox, MonthPicker, ProductCategoryCombobox, DateTypeAutocomplete},
  mixins: [CurrencyMixin],
  data() {
    return {
      loading: false,
      report: [],
      reportDeliveryCost: [],
      stock: null,
      monto: false,
      cantidad: true,
      weekday: ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"],
      date: Dayjs().startOf('month').format('YYYY-MM'),
      productCategories: null,
      dateType: 'CREATED',
      branch: null,
      ready: false,
      reportWarning: null,
      windowHeight: window.innerHeight,
    }
  },
  created() {
    this.fetchDefaultBranch()
    // this.fetchReport()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  computed: {
    tableHeight() {
      const availableHeight = this.windowHeight - 170
      return `${availableHeight}px`
    },
    getDay() {
      return date => {
        return this.weekday[Dayjs(date, 'YYYY-MM-DD').day()]
      }
    },
    getDates() {
      let dates = []
      let today = Dayjs(this.date, 'YYYY-MM')
      let endOfMonth = today.clone().add(1, 'month')
      let counter = 0
      while (endOfMonth.isAfter(today) && counter < 31) {
        dates.push(today.format('YYYY-MM-DD'))
        today = today.add(1, 'day')
        counter++
      }
      return dates
    },
    getDatesOld() {
      return this.report.reduce((a, v) => {
        if (!a.includes(v.date)) a.push(v.date)
        return a
      }, [])
    },
    getBackgroundByDate() {
      return (date, productShortName) => {
        let p = this.getQuantityByDateAndProduct(date, productShortName) * 100 / this.getAverageByDate(date)
        //console.log("p", date, productShortName, p)
        switch (true) {
          case (p > 80):
            return 'blue--text text--darken-4 '
          case (p > 50):
            return 'green--text text--darken-4 '
          case p > 30:
            return 'grey--text text--darken-4 '
          case p > 0:
            return 'grey--text text--darken-4 '
          default:
            return 'grey--text text--darken-1 '
        }
      }
    },
    getBackground() {
      return (productShortName) => {
        let p = this.getQuantityByProduct(productShortName) * 100 / this.getAverage
        switch (true) {
          case (p > 80):
            return 'blue--text text--darken-4 '
          case (p > 50):
            return 'green--text text--darken-4 '
          case p > 30:
            return 'grey--text text--darken-4 '
          case p > 0:
            return 'grey--text text--darken-4 '
          default:
            return 'grey--text text--darken-1 '
        }
      }
    },
    getQuantityByDateAndProduct() {
      return (date, productShortName) => {
        return this.getQuantityIndividualByDateAndProduct(date, productShortName) + this.getQuantityPromoByDateAndProduct(date, productShortName)
      }
    },
    getQuantityIndividualByDateAndProduct() {
      return (date, productShortName) => {
        let r = this.report.find(r => r.date === date && r.product === productShortName)
        return r ? r.quantity : 0
      }
    },
    getQuantityPromoByDateAndProduct() {
      return (date, productShortName) => {
        let r = this.report.find(r => r.date === date && r.productPromo === productShortName)
        return r ? r.quantityPromo : 0
      }
    },
    getQuantityByProductIndividual() {
      return (productShortName) => {
        return this.report.filter(r => r.product === productShortName)
            .reduce((a, r) => a + r.quantity, 0)
      }
    },
    getQuantityByProductPromo() {
      return (productShortName) => {
        return this.report.filter(r => r.productPromo === productShortName)
            .reduce((a, r) => a + r.quantityPromo, 0)
      }
    },
    getQuantityByProduct() {
      return (productShortName) => {
        return this.getQuantityByProductIndividual(productShortName) +  this.getQuantityByProductPromo(productShortName)
      }
    },
    getAmountByProduct() {
      return (productShortName) => {
        return this.report.filter(r => r.product === productShortName)
            .reduce((a, r) => a + r.amount, 0)
      }
    },
    getIsDozen() {
      return (report) => {
        return report.categories && report.categories.length > 0 ? report.categories.some(c => c.dozen) : false
      }
    },
    getDozenByDate() {
      return (date) => {
        let quantity = 0

        this.report.forEach(r => {
          if (r.date === date) {
            const dozenApply = this.getIsDozen(r)
            if (dozenApply) quantity += r.quantity
          }
        })

        return quantity ? Math.round(quantity / 12) : 0
      }
    },
    getAmountByDateAndProduct() {
      return (date, productShortName) => {
        let r = this.report.find(r => r.date === date && r.product === productShortName)
        return r ? r.amount : 0
      }
    },
    getAverageByDate() {
      return (date) => {
        return (this.getQuantityByDate(date) / this.report.filter(r => r.date === date).length)
      }
    },
    getQuantityByDate() {
      return (date) => {
        return this.report.filter(r => r.date === date).reduce((a, v) => {
          // console.log("getQuantityByDate", a, v.quantity, v.quantityPromo, "fin")
          return a + v.quantity + (v.quantityPromo ? v.quantityPromo : 0)
        }, 0)
      }
    },
    getAmountByDate() {
      return (date) => {
        return this.report.filter(r => r.date === date).reduce((a, v) => {
          return a + v.amount
        }, 0)
      }
    },
    getPromoByDate() {
      return (date) => {
        return this.report.filter(r => r.date === date).reduce((a, v) => {
          return a + v.promoCount
        }, 0)
      }
    },
    getDeliveryCostByDate() {
      return (date) => {
        let d = this.reportDeliveryCost.find(r => r.date === date)
        return d ? d.deliveryCost : 0
      }
    },
    //Totals
    getAverage() {
      return (this.getQuantity / this.products.length)
    },

    getDozen() {
      let quantity = this.getQuantity
      return quantity ? Math.round(quantity / 12) : 0
    },
    getQuantity() {
      return this.report.reduce((a, v) => {
        return a + v.quantity + v.quantityPromo
      }, 0)
    },
    getAmount() {
      return this.report.reduce((a, v) => {
        return a + v.amount
      }, 0)
    },
    getDeliveryCost() {
      return this.reportDeliveryCost.reduce((a, v) => {
        return a + v.deliveryCost
      }, 0)
    },
    selectedProductCategoryIds() {
      if (!this.productCategories || this.productCategories.length === 0) return []

      return this.productCategories.map(category => {
        if (typeof category === 'object') return category.id || category._id
        return category
      }).filter(categoryId => !!categoryId)
    },
    products() {
      if (!this.stock || !this.stock.items) return []

      return this.stock.items
          .map(item => item.product)
          .filter(product => this.productHasSelectedCategory(product))
    },

  },

  methods: {
    handleResize() {
      this.windowHeight = window.innerHeight
    },
    fetchDefaultBranch() {
      BranchProvider.findBranchDefault()
          .then(r => {
            this.branch = r.data.findBranchDefault
          })
    },
    productHasSelectedCategory(product) {
      if (!product || !product.categories || this.selectedProductCategoryIds.length === 0) return false

      return product.categories.some(category => {
        const categoryId = typeof category === 'object' ? category.id || category._id : category
        return this.selectedProductCategoryIds.includes(categoryId)
      })
    },
    filterCategories(report) {
      if(this.selectedProductCategoryIds.length === 0) return []
      return report.filter(r => this.selectedProductCategoryIds.some(category => {
        if(!r.categories) return false
        return r.categories.some(c => c._id === category)
      }))
    },
    async fetchReport() {
      try {

        if(!this.branch || !this.dateType) return
        if(this.selectedProductCategoryIds.length === 0) {
          this.ready = false
          this.report = []
          this.reportDeliveryCost = []
          this.reportWarning = this.$t('delivery.salesReport.warnings.requiredCategories')
          return
        }

        this.ready = false
        this.reportWarning = null
        this.loading = true
        let r

        r = await StockProvider.findStockByBranch(this.branch.id)
        this.stock = r.data.findStockByBranch

        const filters = {
          dateType: this.dateType,
          productCategories: this.selectedProductCategoryIds
        }

        r = await SalesReportProvider.deliveryCostReport(this.date, this.branch.id, filters)
        this.reportDeliveryCost = r.data.deliveryCostReport

        r = await SalesReportProvider.salesReport(this.date, this.branch.id, filters)
        this.report = this.filterCategories(r.data.salesReport)

        this.ready = true

      } catch (e) {
        console.error('Error fetching report', e)
      } finally {
        this.loading = false
      }


    },

  }
}
</script>

<style scoped>
tr.border_bottom td {
  border-bottom: 2px solid black !important;
  padding: 1px !important;
}

.sales-report-page {
  max-width: 100%;
  overflow-x: hidden;
}

.sales-report-action {
  min-width: 100%;
}

.sales-report-table-wrapper {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
}

.sales-report-table {
  min-width: max-content;
}

.sales-report-table ::v-deep table {
  width: max-content;
  min-width: 100%;
}

.sales-report-table ::v-deep th,
.sales-report-table ::v-deep td {
  white-space: nowrap;
}

@media (min-width: 1264px) {
  .sales-report-action {
    min-width: 180px;
  }
}
</style>
